// Clipping masks with curves, cut as Rive cuts them at the scale they are drawn at, and split into convex parts once
// rather than every frame: rive-spine's companion runtime (`runtime/riveClipping.ts`, copied 2026-10-13, unchanged but
// for this header and its imports; engine decision 75, `SpineArtboard` installs both).
//
// Rive tessellates a mask's curves as it draws (runtime/riveCurves.ts has its rules), at the scale drawn: a fixed
// polygon misses it by up to 1/4 px along the edge, a few dozen levels where bright texels meet it. The converter keeps the curves (the skeleton JSON's `rive.clips`) beside a polygon cut for
// scale 1; `tessellateRiveClips` re-cuts them at the scale a skeleton is drawn at, whenever that scale changes.
//
// spine-core's SkeletonClipping.clipStart triangulates the clipping polygon and merges the triangles into convex parts
// each time it is called, and spine-pixi-v8 calls it every frame for every clipping attachment it draws: for a ring (its
// inner side all reflex) most of a millisecond. The parts depend on the polygon's world vertices alone: `installClipCache`
// keeps them per attachment and reuses them while those vertices are exactly the same, so a clip that holds still costs a
// comparison, and one that moves (deform keys, a moving bone) costs what it did. What gets clipped is unchanged: the same
// parts, in the same order (copies of spine-core's, which it recycles on its next split).
//
//   installClipCache();                                                   // once, before the first render
//   tessellateRiveClips(coin, skeletonJson, () => pixelsPerUnit);         // per Spine object with curved clips
import { SkeletonClipping, type ClippingAttachment, type Slot, type Spine } from "@esotericsoftware/spine-pixi-v8";
import { riveClipPolygon, type RiveCurveClip } from "./riveCurves";

export { riveClipPolygon, type RiveCurveClip };

/**
 * Re-cuts `spine`'s curved clipping polygons as Rive tessellates them, at `scale` (device pixels per skeleton unit where
 * it is drawn), whenever that scale or a clip bone's transform changes. `json`: the skeleton's JSON as loaded (its
 * `rive.clips`; nothing to do without). Each clipping slot gets its own copy of the attachment, so instances drawn at
 * different scales keep their own polygons.
 */
export function tessellateRiveClips(spine: Spine, json: unknown, scale: () => number): void {
  const clips = (json as { rive?: { clips?: Record<string, RiveCurveClip> } } | null)?.rive?.clips;
  if (!clips) return;
  const entries = Object.entries(clips).flatMap(([name, clip]) => {
    const slot = spine.skeleton.findSlot(name);
    const original = slot?.getAttachment() as ClippingAttachment | null | undefined;
    if (!slot || !original) return [];
    const copy = original.copy() as ClippingAttachment;
    return [{ slot, clip, original, copy, key: "" }];
  });
  const previous = spine.afterUpdateWorldTransforms;
  spine.afterUpdateWorldTransforms = (object: Spine): void => {
    previous(object);
    const s = scale();
    for (const e of entries) {
      if (e.slot.getAttachment() === e.original) e.slot.setAttachment(e.copy);
      if (e.slot.getAttachment() !== e.copy) continue;
      const bone = e.slot.bone;
      const key = `${s} ${bone.a} ${bone.b} ${bone.c} ${bone.d}`;
      if (key === e.key) continue;
      e.key = key;
      e.copy.vertices = riveClipPolygon(e.clip, bone.a, bone.b, bone.c, bone.d, s);
      e.copy.worldVerticesLength = e.copy.vertices.length;
    }
  };
}

interface Parts {
  /** The world vertices they were made from. */
  world: number[];
  /** spine-core's convex parts, each closed (its first point repeated at the end). */
  polygons: number[][];
}

interface Clipper {
  clipAttachment: ClippingAttachment | null;
  clippingPolygons: number[][] | null;
  clipStart(slot: Slot, clip: ClippingAttachment): number;
}

const cache = new WeakMap<ClippingAttachment, Parts>();
let installed = false;

/** Makes SkeletonClipping reuse a clipping polygon's convex parts while its world vertices stay exactly the same. */
export function installClipCache(): void {
  if (installed) return;
  installed = true;
  const proto = SkeletonClipping.prototype as unknown as Clipper;
  const clipStart = proto.clipStart;
  const world: number[] = [];
  proto.clipStart = function (this: Clipper, slot: Slot, clip: ClippingAttachment): number {
    if (this.clipAttachment) return 0;
    const n = clip.worldVerticesLength;
    world.length = n;
    clip.computeWorldVertices(slot, 0, n, world, 0, 2);
    const parts = cache.get(clip);
    if (parts && parts.world.length === n) {
      let same = true;
      for (let i = 0; i < n && same; i++) same = parts.world[i] === world[i];
      if (same) {
        this.clipAttachment = clip;
        this.clippingPolygons = parts.polygons;
        return parts.polygons.length;
      }
    }
    const count = clipStart.call(this, slot, clip);
    const polygons = this.clippingPolygons!.map((polygon) => polygon.slice());
    cache.set(clip, { world: world.slice(), polygons });
    this.clippingPolygons = polygons;
    return count;
  };
}
